import { type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';

import { ownerDocument } from '@mui/material/utils';

import { getKeyboardNavigationKey } from '../../../../util/keyboard';

import * as types from './types';
import * as util from './util';

interface FocusedItemState {
  /**
   * The index of the currently focused item in the list.
   */
  readonly currentFocusedIndex: number;
  readonly handleKeyDown: (e: KeyboardEvent<HTMLDivElement>) => void;
  /**
   * The index of the item that was initially focused when the list was first rendered.  This value
   * is not updated on subsequent renders based on navigation changes.
   */
  readonly initialFocusedIndex: number;
  /**
   * The ref object pointing to the list container element.
   */
  readonly listRef: React.MutableRefObject<HTMLDivElement | null>;
}

export interface UseItemFocusedStateOptions {
  /**
   * Defines whether or not the first focusable item in the list should be automatically focused
   * when the list is first rendered or when the value of this prop changes to 'true'.  If set to
   * 'false', no item will be focused initially, and the keyboard navigation between items in the
   * list will not function until an item is focused programmatically or via user interaction.
   *
   * @default true
   */
  readonly autoFocusItem?: boolean;
}

interface UpdateFocusedItemOptions {
  readonly addClassName?: boolean;
  readonly isInitial: boolean;
}

/**
 * A hook that can be used to manage the focused state of items in a list, including handling
 * keyboard navigation between items using defined keyboard keys,
 * {@link types.ItemNavigationKeyboardKey}.
 */
export const useItemFocusedState = ({
  autoFocusItem = true,
}: UseItemFocusedStateOptions = {}): FocusedItemState => {
  const listRef = useRef<HTMLDivElement | null>(null);
  const [currentFocusedIndex, setCurrentFocusedIndex] = useState(-1);
  const [initialFocusedIndex, setInitialFocusedIndex] = useState(-1);

  const updateFocusedItem = useCallback(
    ({ index, item, siblings }: util.FoundFocusableItemResult, opts: UpdateFocusedItemOptions) => {
      setCurrentFocusedIndex(index);
      if (opts.isInitial) {
        setInitialFocusedIndex(index);
      }
      item.focus();
      if (opts.addClassName) {
        item.classList.add('Mui-focusVisible');
      }
      siblings.forEach(sibling => sibling.classList.remove('Mui-focusVisible'));
    },
    [],
  );

  useEffect(() => {
    if (listRef.current && autoFocusItem) {
      const result = util.findFocusableItem(listRef.current, {
        startFrom: types.ItemNavigationStartingPoint.Top,
      });
      if (result.item) {
        return updateFocusedItem(result, { addClassName: true, isInitial: true });
      }
    }
  }, [updateFocusedItem, autoFocusItem]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const list = listRef.current;
      const key = getKeyboardNavigationKey(e.key);
      const currentFocus = ownerDocument(list).activeElement;

      if (list && key && types.isItemNavigationKeyboardKey(key)) {
        const result = util.getNextFocusableItemFromKeyboardKey(list, currentFocus, key);
        if (result.item) {
          e.preventDefault();
          updateFocusedItem(result, { addClassName: true, isInitial: false });
        }
      }
    },
    [updateFocusedItem],
  );

  return {
    currentFocusedIndex,
    handleKeyDown,
    initialFocusedIndex,
    listRef,
  };
};
